import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import MCheckbox from "../../../components/MCheckbox";
import { Box } from "@mui/material";
import {
  useAnalysisTypes,
  useInstallation,
  useSamplingReason,
  useTypeSample,
  useUser,
} from "@services/servicesGenericsTables";
import { useTypesAccumulation } from "@services/servicesGenericsTables/useTypesAccumulation";
import useFilteredSamplingPoints from "@app/hooks/useFilteredSamplingPoints";
import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { yearsTable } from "@app/utils/tables/years.tables";
import { monthsTable } from "@app/utils/tables/months.tables";
import { weeksTable } from "@app/utils/tables/weeks.tables";
import { yesNoTable } from "@app/utils/tables/shared.tables";
import { CommentsButtons } from "@app/components/Actions";

const SampleForm = () => {
  const { installationList } = useInstallation();
  const { samplingReasonList } = useSamplingReason();
  const { typesAccumulationList } = useTypesAccumulation();
  const { sampleTypeList } = useTypeSample();
  const { analysisTypeList } = useAnalysisTypes();
  const { userList } = useUser();
  const form = useFormContext();

  const filteredAnalysisTypeList = useMemo(() => {
    return analysisTypeList.filter((item) => item.confirmed_analysis);
  }, [analysisTypeList]);

  const isNew = !form.getValues("id");

  const installation = form.watch("installation");
  const year = form.watch("year");

  const filteredSamplingPoints = useFilteredSamplingPoints(
    installation,
    "sampling_points"
  );

  useEffect(() => {
    if (isNew) {
      if (installation && year) {
        const clientReference = `${year}-${installation.id}-XXXX`;
        form.setValue("masl_reference", clientReference);
      } else {
        form.setValue("masl_reference", "");
      }
    }
  }, [installation, year]);

  return (
    <>
      <ResponsiveFormContainer columns={5}>
        <MAutocomplete
          key={JSON.stringify(form.getValues("analyst"))}
          name="analyst"
          options={userList}
          label="Seleccione un analista"
          getOptionLabel={(option) => option.username || ""}
          validation={{ required: "El analista es requerido" }}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("installation"))}
          name="installation"
          options={installationList}
          label="Seleccione una instalación"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          validation={{ required: "La instalación es requerida" }}
        />

        <MDatePicker
          name="high_date"
          pickerType="date"
          label="Fecha de alta"
          validation={{ required: "La fecha de alta es requerida" }}
        />

        <MAutocomplete
          key={JSON.stringify(form.getValues("year"))}
          name="year"
          options={yearsTable}
          label="Seleccione un año"
          validation={{ required: "El año es requerido" }}
        />

        <MAutocomplete
          key={JSON.stringify(form.getValues("month"))}
          name="month"
          options={monthsTable}
          label="Seleccione un mes"
          validation={{ required: "El mes es requerido" }}
        />

        <MAutocomplete
          key={JSON.stringify(form.getValues("week"))}
          name="week"
          options={weeksTable}
          label="Seleccione una semana"
          validation={{ required: "La semana es requerida" }}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("reason"))}
          name="reason"
          options={samplingReasonList}
          label="Seleccione un motivo"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          validation={{ required: "El motivo es requerido" }}
        />

        <MAutocomplete
          key={JSON.stringify(filteredSamplingPoints)}
          name="sampling_points"
          options={filteredSamplingPoints}
          label="Punto de toma de muestra"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          validation={{
            required: "El punto de toma de muestra es requerido",
          }}
          helperText={!installation ? "Seleccione una instalación" : ""}
          disabled={!installation}
        />

        <MAutocomplete
          key={JSON.stringify(form.getValues("sample_type"))}
          name="sample_type"
          options={sampleTypeList}
          label="Seleccione un tipo de muestra"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          validation={{ required: "El tipo de muestra es requerido" }}
        />

        <MAutocomplete
          name="shared"
          options={yesNoTable}
          label="Seleccione si es compartida"
          getOptionLabel={(option) => option.label || ""}
          validation={{ required: "La muestra compartida es requerida" }}
        />
        <MTextField
          name="client_reference"
          label="Referencia del cliente"
          placeholder="Referencia del cliente"
          type="text"
          fullWidth
        />

        <MTextField
          key={JSON.stringify(form.getValues("masl_reference"))}
          name="masl_reference"
          placeholder="Referencia MASL"
          label="Referencia MASL"
          type="text"
          fullWidth
          disabled
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("accumulation"))}
          name="accumulation"
          options={typesAccumulationList}
          label="Seleccione una acumulación"
          getOptionLabel={(option) => `${option.code} - ${option.name}`}
          validation={{ required: "La acumulación es requerida" }}
        />
        <MTextField
          key={JSON.stringify(form.getValues("sample_number"))}
          name="sample_number"
          label="Número de muestras"
          placeholder="Número de muestras"
          type="number"
          fullWidth
          validation={{ required: "El número de muestras es requerido" }}
        />
      </ResponsiveFormContainer>
      <Box sx={{ mt: 2 }}>
        <MCheckbox
          name="analysis_type"
          options={filteredAnalysisTypeList.map((item) => ({
            label: item.codigo,
            value: item.id,
          }))}
          label="Análisis a realizar"
          validation={{ required: "Debe seleccionar al menos una opción" }}
          multiple={true}
          orientation="horizontal"
          columns={5}
        />
      </Box>
      {isNew ? (
        <ResponsiveFormContainer columns={4} mt={2}>
          <MTextField
            key={JSON.stringify(form.getValues("comments_internal"))}
            name="comments_internal"
            label="Comentarios internos"
            placeholder="Comentarios internos"
            type="textArea"
            fullWidth
            multiline
            rows={4}
          />
          <MTextField
            key={JSON.stringify(form.getValues("comments_external"))}
            name="comments_external"
            label="Comentarios a informes"
            placeholder="Comentarios a informes"
            type="textArea"
            rows={4}
            multiline
            fullWidth
          />
        </ResponsiveFormContainer>
      ) : (
        <CommentsButtons idSample={form.getValues("id")} />
      )}
    </>
  );
};

export default SampleForm;
